// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { EmptyState } from '@/components/empty-state';
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import {
  Add as AddIcon,
  ArrowOutwardRounded,
  ExpandLess,
  ExpandMore,
  SwapHoriz as SwapHorizIcon,
} from '@/constants/hardfin-standard-icons';
import { InventoryEntry, getTotalCount } from '@/inventory/api/types';
import { useInventory } from '@/inventory/hooks';
import { getItem } from '@/items/api/get-item';
import { ItemDetail } from '@/items/api/types';
import { AdjustInventoryDialog } from '@/items/components/adjust-inventory-dialog';
import { AdjustInventoryProvider } from '@/items/components/adjust-inventory-provider';
import { InventoryLotDetails } from '@/items/components/inventory-lot-details';
import { InventoryRowActions } from '@/items/components/inventory-row-actions';
import { TransferInventoryDialog } from '@/items/components/transfer-inventory-dialog';
import { TransferInventoryProvider } from '@/items/components/transfer-inventory-provider';
import { useAdjustInventory } from '@/items/hooks/use-adjust-inventory';
import { useTransferInventory } from '@/items/hooks/use-transfer-inventory';
import { getEffectiveLocationName } from '@/location/utilities/get-effective-third-party';
import { SparseItem, SparseLocation } from '@/sparse/api/types';
import { extractSparseItem } from '@/sparse/utilities/to-sparse-item';
import { formatNumber } from '@/utilities/number';
import {
  Button,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Navigate, createFileRoute } from '@tanstack/react-router';
import { Fragment, useState } from 'react';

export const Route = createFileRoute('/_app/catalog/items/$itemKey/units')({
  component: ItemInventoryRouteWithDialogs,
});

function ItemInventoryRouteWithDialogs() {
  return (
    <AdjustInventoryProvider>
      <TransferInventoryProvider>
        <ItemInventoryRouteContent />
      </TransferInventoryProvider>
    </AdjustInventoryProvider>
  );
}

function ItemInventoryRouteContent() {
  const { itemKey } = Route.useParams();

  const { data } = useSuspenseQuery({
    queryKey: ['item', itemKey],
    queryFn: () => getItem(itemKey),
  });

  const {
    isOpen: isAdjustOpen,
    item: adjustItem,
    location: adjustLocation,
    closeDialog: closeAdjustDialog,
  } = useAdjustInventory();

  const {
    isOpen: isTransferOpen,
    item: transferItem,
    location: transferLocation,
    closeDialog: closeTransferDialog,
  } = useTransferInventory();

  // Only bulk items have inventory tab (isPhysical && !hasAssets)
  if (!data.item.isPhysical || data.item.hasAssets) {
    return (
      <Navigate to="/catalog/items/$itemKey" params={{ itemKey }} replace />
    );
  }

  return (
    <>
      <ItemInventoryContent item={data.item} />
      <AdjustInventoryDialog
        isOpen={isAdjustOpen}
        item={adjustItem}
        location={adjustLocation}
        onClose={closeAdjustDialog}
      />
      <TransferInventoryDialog
        isOpen={isTransferOpen}
        item={transferItem}
        location={transferLocation}
        onClose={closeTransferDialog}
      />
    </>
  );
}

interface ItemInventoryContentProps {
  item: ItemDetail;
}

function ItemInventoryContent(props: ItemInventoryContentProps) {
  const { openDialog: openAdjustDialog } = useAdjustInventory();
  const { openDialog: openTransferDialog } = useTransferInventory();

  const sparseItem = extractSparseItem(props.item);

  // Always use location view for bulk item inventory
  // Include non-inventory locations since we want to see all locations where this item exists
  const result = useInventory({
    type: 'bulk-items',
    dimension: 'location',
    filters: {
      itemKeys: [props.item.key],
      includeNonInventory: true,
    },
  });

  // Extract location inventory data
  const locations =
    result.view === 'location' ? (result.inventory?.data.results ?? []) : [];

  return (
    <HardfinTabCardWrapper
      title="Units on location"
      actions={
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon fontSize="small" />}
            onClick={() =>
              openAdjustDialog({
                item: sparseItem,
                location: undefined,
              })
            }
          >
            Adjust
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<SwapHorizIcon fontSize="small" />}
            onClick={() =>
              openTransferDialog({
                item: sparseItem,
                location: undefined,
              })
            }
          >
            Transfer
          </Button>
        </Stack>
      }
    >
      <Stack padding={2}>
        <SupportErrorBoundary message="Failed to load inventory">
          <BulkItemInventoryTable
            itemKey={props.item.key}
            item={sparseItem}
            locations={locations}
            isLoading={result.isLoading}
            isFetching={result.isFetching}
          />
        </SupportErrorBoundary>
      </Stack>
    </HardfinTabCardWrapper>
  );
}

interface LocationInventoryRow {
  location: SparseLocation;
  items: {
    bulkItems: {
      inventory: InventoryEntry[];
    }[];
  };
}

interface BulkItemInventoryTableProps {
  itemKey: string;
  item: SparseItem;
  locations: LocationInventoryRow[];
  isLoading?: boolean;
  isFetching?: boolean;
}

/**
 * Table for displaying bulk item inventory by location.
 * Shows location name, quantity on hand, actions, and expandable lot details.
 */
function BulkItemInventoryTable(props: BulkItemInventoryTableProps) {
  const { itemKey, item, locations, isLoading, isFetching } = props;
  const [expandedLots, setExpandedLots] = useState<Record<string, boolean>>({});

  const toggleLotExpand = (locationKey: string) => {
    setExpandedLots((prev) => ({
      ...prev,
      [locationKey]: !prev[locationKey],
    }));
  };

  if (isLoading) {
    return (
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="55%" sx={headerCellStyle}>
                Location
              </TableCell>
              <TableCell width="20%" align="right" sx={headerCellStyle}>
                On hand
              </TableCell>
              <TableCell width="25%" align="right" sx={headerCellStyle}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({ length: 5 }).map((_, index) => (
              <TableRow key={index}>
                <TableCell>
                  <Skeleton variant="text" width="60%" />
                </TableCell>
                <TableCell align="right">
                  <Skeleton variant="text" width="40%" />
                </TableCell>
                <TableCell align="right">
                  <Skeleton variant="rounded" width={80} height={32} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  if (!locations || locations.length === 0) {
    return (
      <EmptyState title="No inventory found">
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography color="text.secondary">
            Use the adjust button to add inventory at a location
          </Typography>
          <ArrowOutwardRounded fontSize="small" color="action" />
        </Stack>
      </EmptyState>
    );
  }

  return (
    <>
      {isFetching && <LinearProgress />}
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="55%" sx={headerCellStyle}>
                Location
              </TableCell>
              <TableCell width="20%" align="right" sx={headerCellStyle}>
                On hand
              </TableCell>
              <TableCell width="25%" align="right" sx={headerCellStyle}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {locations.map((row) => {
              // Sum quantities for this bulk item at this location
              const quantity = row.items.bulkItems.reduce(
                (sum, item) => sum + getTotalCount(item.inventory),
                0
              );
              const isExpanded = expandedLots[row.location.key] ?? false;

              return (
                <Fragment key={row.location.key}>
                  <TableRow>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Tooltip
                          arrow
                          title={isExpanded ? 'Hide lots' : 'Show lots'}
                        >
                          <IconButton
                            size="small"
                            onClick={() => toggleLotExpand(row.location.key)}
                            aria-label={
                              isExpanded
                                ? 'Collapse lot details'
                                : 'Expand lot details'
                            }
                          >
                            {isExpanded ? (
                              <ExpandLess fontSize="small" />
                            ) : (
                              <ExpandMore fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Typography variant="body2">
                          {getEffectiveLocationName(row.location)}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatNumber(quantity)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <InventoryRowActions
                        location={row.location}
                        item={item}
                      />
                    </TableCell>
                  </TableRow>
                  <InventoryLotDetails
                    itemKey={itemKey}
                    locationKey={row.location.key}
                    expanded={isExpanded}
                  />
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

const headerCellStyle = {
  fontSize: '12px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
} as const;
